/**
 * Author: Claude Code
 * Generation: Step 2 - Agent attributes allocation with 100 points system
 * Version: v3.2.44
 * Modified: 2025-11-04
 */
import { useState, useEffect } from 'react';
import { useRoomStore } from '@store/RoomStore';

export default function Step2Attributes() {
  const {
    myAgentConfiguration,
    updateAgentConfiguration,
    getRemainingAttributePoints,
  } = useRoomStore();

  // v3.2.44: Separate preview state from committed state for smooth dragging
  const [localAttributes, setLocalAttributes] = useState(
    myAgentConfiguration || { hp: 30, attack: 25, defense: 20, speed: 10, style: 7 }
  );

  // Sync local state when Store configuration changes
  useEffect(() => {
    if (myAgentConfiguration) {
      setLocalAttributes(myAgentConfiguration);
    }
  }, [myAgentConfiguration]);

  // Preview handler - clamp to max allowed based on remaining points
  const handleAttributePreview = (attr: 'hp' | 'attack' | 'defense' | 'speed', value: number) => {
    // Calculate sum of other three attributes
    const otherAttrs = { hp: localAttributes.hp, attack: localAttributes.attack, defense: localAttributes.defense, speed: localAttributes.speed };
    const othersSum = Object.entries(otherAttrs)
      .filter(([key]) => key !== attr)
      .reduce((sum, [, v]) => sum + v, 0);

    // Max allowed value for this attribute = 100 - sum of others
    const maxAllowed = 100 - othersSum;
    const clampedValue = Math.max(0, Math.min(value, maxAllowed));

    setLocalAttributes(prev => ({
      ...prev,
      [attr]: clampedValue,
    }));
  };

  // Commit handler - validate and apply to Store only on release
  const handleAttributeCommit = (attr: 'hp' | 'attack' | 'defense' | 'speed') => {
    if (!myAgentConfiguration) return;

    const newTotal = localAttributes.hp + localAttributes.attack + localAttributes.defense + localAttributes.speed;

    // Only apply to Store if total doesn't exceed 100
    if (newTotal <= 100) {
      updateAgentConfiguration(localAttributes);
    } else {
      // Rollback to last valid Store state if validation fails
      setLocalAttributes(myAgentConfiguration);
    }
  };

  const handleAggressivenessPreview = (value: number) => {
    setLocalAttributes(prev => ({
      ...prev,
      style: value,
    }));
  };

  const handleAggressivenessCommit = () => {
    updateAgentConfiguration(localAttributes);
  };

  const remainingPoints = Math.max(0, 100 - (localAttributes.hp + localAttributes.attack + localAttributes.defense + localAttributes.speed));

  return (
    <div className="step-content active">
      <div className="step-header">
        <h3 className="step-title">Distribute Points</h3>
        <p className="step-subtitle">Allocate 100 points across your agent's attributes</p>
      </div>

      <div className="points-badge">
        <div className="points-label">Points Remaining</div>
        <div className="points-value">
          <span>{remainingPoints}</span> / 100
        </div>
      </div>

      <div className="attributes-section">
        {/* HP */}
        <div className="attribute-item">
          <div className="attribute-header">
            <span className="attribute-name">❤️ Health</span>
            <span className="attribute-value">{localAttributes.hp}</span>
          </div>
          <div className="slider-container">
            <div className="slider-track">
              <div className="slider-fill" style={{ width: `${localAttributes.hp}%` }}></div>
            </div>
            <input
              type="range"
              className="slider"
              min="0"
              max="100"
              value={localAttributes.hp}
              onChange={(e) => handleAttributePreview('hp', parseInt(e.target.value))}
              onMouseUp={() => handleAttributeCommit('hp')}
              onTouchEnd={() => handleAttributeCommit('hp')}
            />
          </div>
        </div>

        {/* Attack */}
        <div className="attribute-item">
          <div className="attribute-header">
            <span className="attribute-name">⚔️ Attack</span>
            <span className="attribute-value">{localAttributes.attack}</span>
          </div>
          <div className="slider-container">
            <div className="slider-track">
              <div className="slider-fill" style={{ width: `${localAttributes.attack}%` }}></div>
            </div>
            <input
              type="range"
              className="slider"
              min="0"
              max="100"
              value={localAttributes.attack}
              onChange={(e) => handleAttributePreview('attack', parseInt(e.target.value))}
              onMouseUp={() => handleAttributeCommit('attack')}
              onTouchEnd={() => handleAttributeCommit('attack')}
            />
          </div>
        </div>

        {/* Defense */}
        <div className="attribute-item">
          <div className="attribute-header">
            <span className="attribute-name">🛡️ Defense</span>
            <span className="attribute-value">{localAttributes.defense}</span>
          </div>
          <div className="slider-container">
            <div className="slider-track">
              <div className="slider-fill" style={{ width: `${localAttributes.defense}%` }}></div>
            </div>
            <input
              type="range"
              className="slider"
              min="0"
              max="100"
              value={localAttributes.defense}
              onChange={(e) => handleAttributePreview('defense', parseInt(e.target.value))}
              onMouseUp={() => handleAttributeCommit('defense')}
              onTouchEnd={() => handleAttributeCommit('defense')}
            />
          </div>
        </div>

        {/* Speed */}
        <div className="attribute-item">
          <div className="attribute-header">
            <span className="attribute-name">⚡ Speed</span>
            <span className="attribute-value">{localAttributes.speed}</span>
          </div>
          <div className="slider-container">
            <div className="slider-track">
              <div className="slider-fill" style={{ width: `${localAttributes.speed}%` }}></div>
            </div>
            <input
              type="range"
              className="slider"
              min="0"
              max="100"
              value={localAttributes.speed}
              onChange={(e) => handleAttributePreview('speed', parseInt(e.target.value))}
              onMouseUp={() => handleAttributeCommit('speed')}
              onTouchEnd={() => handleAttributeCommit('speed')}
            />
          </div>
        </div>
      </div>

      {/* Aggressiveness Section - Independent */}
      <div className="section-divider">
        <span className="section-divider-text">Personality Traits</span>
      </div>

      <div className="personality-section">
        <div className="personality-title">
          🔥 Aggressiveness
          <span className="personality-badge">Independent</span>
        </div>
        <p className="personality-desc">
          Define your agent's combat mentality. This is independent from base stats and affects
          decision-making style in battle.
        </p>
        <div className="attribute-item">
          <div className="attribute-header">
            <span className="attribute-name">Passive ← → Aggressive</span>
            <span className="attribute-value">{localAttributes.style}</span>
          </div>
          <div className="slider-container">
            <div className="slider-track">
              <div className="slider-fill" style={{ width: `${localAttributes.style * 10}%` }}></div>
            </div>
            <input
              type="range"
              className="slider"
              min="0"
              max="10"
              value={localAttributes.style}
              onChange={(e) => handleAggressivenessPreview(parseInt(e.target.value))}
              onMouseUp={handleAggressivenessCommit}
              onTouchEnd={handleAggressivenessCommit}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
